import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, get, post, put } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { Badge, Card, ConfirmDialog, Empty, Failure, Field, Loading, Notice, Stat } from '../components/ui.js';
import { CommandProgress, type TrackedCommand } from '../components/CommandProgress.js';
import { DEFAULT_SCENARIO, ScenarioEditor, type BellScenario } from '../components/ScenarioEditor.js';
import {
  ACK_STAGE_LABELS,
  BELL_ACK_CODE_LABELS,
  BELL_COMMAND_LABELS,
  BELL_DETAIL_VALUE_LABELS,
  DEVICE_EVENT_LABELS,
  READINESS,
  WEEKDAYS,
  formatBytes,
  formatDateTime,
  formatDuration,
  formatRelative,
} from '../lib/format.js';

/*
 * Bell device (esp32-dfplayer-bell) console.
 *
 * The device polls the platform every couple of seconds, so this page polls at
 * the same pace and counts the phase timer down locally between samples. Every
 * button here queues a command; the table below shows whether the device
 * actually executed it — "sent" is never presented as "played".
 */

interface BellStart {
  time: string;
  days: number[];
}

interface BellConfig {
  source: 'template' | 'manual';
  timing: 'clock' | 'after_bell';
  lesson_count: number;
  lesson_seconds: number[];
  break_seconds: number[];
  volume: number;
  auto_start_enabled: boolean;
  starts: BellStart[];
  skip_holidays: boolean;
  scenario: BellScenario;
}

interface BellState {
  phase?: string;
  lesson?: number;
  timer_running?: boolean;
  remaining_ms?: number;
  duration_ms?: number;
  sequence?: string;
  emergency?: boolean;
  emergency_local?: boolean;
  emergency_pending?: boolean;
  incident_id?: string | null;
  return_message?: boolean;
  audio_playing?: boolean;
  track?: number;
  owner?: string;
  volume?: number;
  rtc_ok?: boolean;
  player_ok?: boolean;
  rtc_local?: string | null;
  clock_source?: string;
  wifi_rssi?: number;
  ip?: string;
  ssid?: string;
  uptime_ms?: number;
  free_heap?: number;
  config_rev?: string;
  events_dropped?: number;
  serial?: string;
  audio_hw?: 'dfplayer' | 'i2s_sd';
  assets_rev?: string;
  assets_total?: number;
  assets_ready?: number;
  missing_tracks?: number[];
  card_tracks?: number[];
  downloading_track?: number;
  sd_total_mb?: number;
  sd_used_mb?: number;
  flash_kb?: number;
  app_kb?: number;
  app_free_kb?: number;
  wifi_scan?: Array<{ ssid: string; rssi: number }>;
  wifi_slot?: 'primary' | 'fallback';
  ota_stage?: 'idle' | 'downloading' | 'verifying' | 'rebooting' | 'failed';
  ota_progress?: number;
  ota_version?: string;
}

interface WireShift {
  t: number;
  l: number[];
  b: number[];
}

interface ScheduleView {
  source: 'template' | 'manual';
  template: { id: string; name: string; assignment_source: string } | null;
  week: WireShift[][];
  overrides: Array<{ date: number; s: WireShift[]; label: string }>;
  problems: string[];
}

interface SoundSetView {
  rev: string;
  synced_card: boolean;
  applied: boolean;
  entries: Array<{ track: number; asset_id: string; name: string; bytes: number; duration_ms: number }>;
}

interface BellLive {
  server_time: string;
  config: BellConfig;
  config_updated_at: string | null;
  effective_config: { rev: string; holidays: number[]; utc_offset_min: number };
  config_applied: boolean;
  state: BellState | null;
  state_at: string | null;
  last_seen_at: string | null;
  online: boolean;
  readiness: string;
  reasons: string[];
  boot_id: string | null;
  firmware_version: string | null;
  poll_ms: number;
  sound_set: SoundSetView;
  schedule: ScheduleView;
}

export interface BellDeviceSummary {
  id: string;
  name: string;
  serial: string;
  status: string;
  school: { id: string; name: string; timezone: string };
  recent_events: Array<{
    type: string;
    occurred_at: string;
    received_at: string;
    clock_reliable: boolean;
    details: Record<string, unknown>;
  }>;
}

interface CommandRow {
  command_id: string;
  type: string;
  payload: Record<string, unknown> | null;
  issued_at: string;
  ack_stage: string | null;
  ack_code: string | null;
  ack_at: string | null;
  ack_detail: string | null;
  reason: string | null;
}

type CommandType =
  | 'start_day'
  | 'reset_day'
  | 'skip'
  | 'play_anthem'
  | 'stop_audio'
  | 'play_track'
  | 'start_lesson'
  | 'set_volume'
  | 'start_emergency'
  | 'cancel_emergency'
  | 'sync_time'
  | 'reboot'
  | 'request_status'
  | 'set_wifi'
  | 'scan_wifi'
  | 'ota_update'
  | 'sync_assets'
  | 'delete_track';

interface PendingCommand {
  type: CommandType;
  lesson?: number;
  track?: number;
  volume?: number;
  ssid?: string;
  password?: string;
  firmware_id?: string;
  title: string;
  message: ReactNode;
  danger?: boolean;
}

const PHASE_LABELS: Record<string, string> = {
  idle: 'لم يبدأ اليوم',
  lesson: 'حصة',
  break: 'استراحة',
  finished: 'انتهى اليوم الدراسي',
};

const SEQUENCE_LABELS: Record<string, string> = {
  none: '—',
  day_start: 'مقاطع بداية اليوم',
  lesson_start: 'مقاطع بداية الحصة',
  lesson_end: 'مقاطع نهاية الحصة',
  day_end: 'مقاطع نهاية اليوم',
  short_anthem: 'النشيد المختصر (0002)',
  lesson_start_voice: 'إعلان بداية الحصة',
  lesson_start_bell: 'جرس بداية الحصة (0004)',
  lesson_end_voice: 'إعلان نهاية الحصة',
  break_bell: 'جرس الاستراحة (0005)',
};

const OWNER_LABELS: Record<string, string> = {
  none: 'لا شيء',
  normal: 'تسلسل اليوم الدراسي',
  emergency: 'الطوارئ',
  emergency_end: 'رسالة انتهاء الطوارئ',
  full_anthem: 'النشيد الكامل',
  manual: 'تشغيل يدوي',
};

const CLOCK_LABELS: Record<string, { label: string; tone: string; icon: string }> = {
  server: { label: 'مزامَنة مع المنصة', tone: 'ok', icon: '✔' },
  ntp: { label: 'مزامَنة عبر الإنترنت (NTP)', tone: 'ok', icon: '✔' },
  rtc: { label: 'من ساعة RTC فقط', tone: 'warn', icon: '▲' },
  none: { label: 'غير مضبوطة', tone: 'danger', icon: '✖' },
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${pad2(m)}:${pad2(s)}`;
}

function formatUptime(ms: number | undefined): string {
  if (ms === undefined) return '—';
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  return d > 0 ? `${d} يوم ${h} ساعة` : h > 0 ? `${h} ساعة ${m} دقيقة` : `${m} دقيقة`;
}

function formatHoliday(n: number): string {
  const s = String(n);
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

function describeDetails(details: Record<string, unknown>): string {
  return Object.entries(details)
    .filter(([k]) => k !== 'command_id')
    .map(([k, v]) => {
      const value = typeof v === 'string' ? (BELL_DETAIL_VALUE_LABELS[v] ?? v) : String(v);
      return `${BELL_DETAIL_VALUE_LABELS[k] ?? k}: ${value}`;
    })
    .join(' · ');
}

function commandLabel(c: CommandRow): string {
  const base = BELL_COMMAND_LABELS[c.type] ?? c.type;
  const p = c.payload ?? {};
  const arg = p.lesson ?? p.track ?? p.volume;
  return arg !== undefined ? `${base} (${String(arg)})` : base;
}

export function BellDevicePanel({ device }: { device: BellDeviceSummary }): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingCommand | null>(null);
  const [reason, setReason] = useState('');
  const [flash, setFlash] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);
  const [tracked, setTracked] = useState<TrackedCommand | null>(null);
  const [trackInput, setTrackInput] = useState(1);
  const [lessonInput, setLessonInput] = useState(1);
  const [volumeInput, setVolumeInput] = useState<number | null>(null);
  const [, setTick] = useState(0);

  const live = useQuery({
    queryKey: ['bell-live', device.id],
    queryFn: () => get<BellLive>(`/devices/${device.id}/bell`),
    refetchInterval: 2000,
  });

  const commands = useQuery({
    queryKey: ['device-commands', device.id],
    queryFn: () => get<{ items: CommandRow[] }>(`/devices/${device.id}/commands?page_size=15`),
    refetchInterval: 3000,
  });

  // Repaint the countdown between samples.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, []);

  const send = useMutation({
    mutationFn: (cmd: PendingCommand) =>
      post<{ note: string; command_id: string; expires_at: string | null }>(`/devices/${device.id}/bell/commands`, {
        type: cmd.type,
        ...(cmd.lesson !== undefined ? { lesson: cmd.lesson } : {}),
        ...(cmd.track !== undefined ? { track: cmd.track } : {}),
        ...(cmd.volume !== undefined ? { volume: cmd.volume } : {}),
        ...(cmd.ssid !== undefined ? { ssid: cmd.ssid, password: cmd.password ?? '' } : {}),
        ...(cmd.firmware_id !== undefined ? { firmware_id: cmd.firmware_id } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: (res, cmd) => {
      setPending(null);
      setReason('');
      setFlash(null);
      setTracked({
        deviceId: device.id,
        commandId: res.command_id,
        deviceName: device.name,
        action: BELL_COMMAND_LABELS[`bell.${cmd.type}`] ?? cmd.type,
        expiresAt: res.expires_at,
      });
      void queryClient.invalidateQueries({ queryKey: ['device-commands', device.id] });
    },
    onError: (e: Error) => {
      setPending(null);
      setFlash({ tone: 'danger', text: e.message });
    },
  });

  if (live.isLoading) return <Loading />;
  if (live.error) return <Failure error={live.error} onRetry={() => void live.refetch()} />;
  const data = live.data!;
  const s = data.state;
  const tz = device.school.timezone;
  const r = READINESS[data.readiness] ?? READINESS.unknown!;
  const clock = CLOCK_LABELS[s?.clock_source ?? 'none'] ?? CLOCK_LABELS.none!;
  const lessonCount = data.config.lesson_count;

  // Remaining time: the sample's value, aged by server time at fetch, then by local time since.
  let remaining: number | null = null;
  if (s?.timer_running && s.remaining_ms !== undefined && data.state_at) {
    const ageAtFetch = Date.parse(data.server_time) - Date.parse(data.state_at);
    const sinceFetch = Date.now() - live.dataUpdatedAt;
    remaining = data.online ? s.remaining_ms - ageAtFetch - sinceFetch : s.remaining_ms;
  }

  const canPlay = can('playback.manual');
  const canManage = can('device.manage');
  const active = device.status === 'ACTIVE';
  const emergency = Boolean(s?.emergency || s?.emergency_pending);

  const ask = (cmd: PendingCommand): void => {
    setFlash(null);
    setPending(cmd);
  };

  return (
    <>
      {flash ? <Notice tone={flash.tone}>{flash.text}</Notice> : null}
      {tracked ? <CommandProgress key={tracked.commandId} command={tracked} /> : null}

      {!active ? (
        <Notice tone="warn">
          الجهاز لم يُسجَّل بعد. من صفحة <Link to="/devices">الأجهزة</Link> اضغط «رمز تسجيل»، ثم اكتب في
          Serial Monitor على الجهاز: <span className="mono">enroll &lt;الرمز&gt;</span>
        </Notice>
      ) : null}

      {emergency ? (
        <Notice tone="danger">
          ⚠ الجهاز في حالة طوارئ
          {s?.emergency_pending ? ' (بانتظار انتهاء رسالة العودة لبدء الصفارة)' : ''}.{' '}
          {s?.incident_id ? (
            <>
              مصدرها حادثة من المنصة — <Link to="/emergency">شاشة الطوارئ</Link>.
            </>
          ) : null}
          {s?.emergency_local ? ' رُفعت من زر الطوارئ على الجهاز أو من هذه الصفحة.' : ''}
        </Notice>
      ) : null}

      {s?.return_message ? (
        <Notice tone="info">يُشغَّل الآن تسلسل انتهاء الطوارئ (0020 ← 0021 مرتين) ثم يعود الجدول.</Notice>
      ) : null}

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <Stat
          label="الاتصال بالمنصة"
          value={
            data.online ? (
              <Badge tone="ok" icon="✔">
                متصل
              </Badge>
            ) : (
              <Badge tone="warn" icon="▲">
                منقطع
              </Badge>
            )
          }
          hint={`آخر مزامنة ${formatRelative(data.last_seen_at)}`}
        />
        <Stat
          label="المرحلة الحالية"
          value={
            <span>
              {PHASE_LABELS[s?.phase ?? 'idle'] ?? s?.phase ?? '—'}
              {s?.phase === 'lesson' || s?.phase === 'break' ? (
                <span className="num"> {s.lesson}</span>
              ) : null}
            </span>
          }
          hint={s?.sequence && s.sequence !== 'none' ? SEQUENCE_LABELS[s.sequence] : undefined}
        />
        <Stat
          label="الوقت المتبقي"
          value={<span className="num">{remaining !== null ? formatClock(remaining) : '—'}</span>}
          hint={
            s?.timer_running && s.duration_ms
              ? `من ${formatClock(s.duration_ms)}${emergency ? ' — يستمر أثناء الطوارئ' : ''}`
              : 'لا يوجد عداد يعمل'
          }
        />
        <Stat
          label="الصوت"
          value={
            s?.audio_playing ? (
              <Badge tone={s.owner === 'emergency' ? 'danger' : 'info'} icon="▶">
                <span className="num">{String(s.track ?? 0).padStart(4, '0')}</span>
              </Badge>
            ) : (
              <Badge tone="muted" icon="■">
                متوقف
              </Badge>
            )
          }
          hint={s?.audio_playing ? OWNER_LABELS[s.owner ?? 'none'] : `مستوى الصوت ${s?.volume ?? '—'}`}
        />
      </div>

      <div className="row" style={{ marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <Badge tone={r.tone} icon={r.icon}>
          {r.label}
        </Badge>
        {data.config_applied ? (
          <Badge tone="ok" icon="✔">
            الإعدادات مطبّقة على الجهاز
          </Badge>
        ) : s ? (
          <Badge tone="warn" icon="…">
            الإعدادات الأخيرة لم تصل بعد
          </Badge>
        ) : null}
      </div>

      {data.reasons.length > 0 ? (
        <Notice tone={data.readiness === 'not_ready' ? 'danger' : 'warn'}>
          <ul style={{ margin: 0, paddingInlineStart: 18 }}>
            {data.reasons.map((reasonText, i) => (
              <li key={i}>{reasonText}</li>
            ))}
          </ul>
        </Notice>
      ) : null}

      <Card title="التحكم عن بعد" subtitle="كل زر يطابق زرًا على الجهاز أو يضيف وظيفة؛ يصل خلال ثوانٍ">
        {!active ? (
          <Empty title="الأوامر متاحة بعد تسجيل الجهاز" />
        ) : (
          <div className="grid cols-2">
            <div>
              <h3 style={{ marginTop: 0 }}>اليوم الدراسي</h3>
              <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                <button
                  className="primary"
                  disabled={!canPlay || emergency}
                  onClick={() =>
                    ask({
                      type: 'start_day',
                      title: 'بدء اليوم الدراسي',
                      message:
                        'يعادل الضغط المطوّل على زر المحاكاة: يوقف أي تشغيل عادي ويبدأ من رسالة بداية اليوم (0001) ثم النشيد المختصر ثم الحصة الأولى.',
                    })
                  }
                >
                  بدء اليوم
                </button>
                <button
                  disabled={!canPlay || emergency || !s?.timer_running}
                  onClick={() =>
                    ask({
                      type: 'skip',
                      title: 'تخطي المرحلة الحالية',
                      message: 'يعادل الضغط القصير على زر SKIP: ينهي الحصة أو الاستراحة الجارية الآن.',
                    })
                  }
                >
                  تخطي (SKIP)
                </button>
                <button
                  disabled={!canPlay || emergency}
                  onClick={() =>
                    ask({
                      type: 'reset_day',
                      title: 'إيقاف اليوم الدراسي',
                      message: 'يوقف تسلسل اليوم والعدادات ويعيد الجهاز إلى وضع الانتظار. لا يلغي الطوارئ.',
                    })
                  }
                >
                  إيقاف اليوم
                </button>
              </div>
              <div className="row" style={{ marginTop: 10, gap: 8 }}>
                <select
                  aria-label="رقم الحصة"
                  value={lessonInput}
                  onChange={(e) => setLessonInput(Number(e.target.value))}
                  style={{ width: 'auto' }}
                >
                  {Array.from({ length: lessonCount }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      الحصة {n}
                    </option>
                  ))}
                </select>
                <button
                  disabled={!canPlay || emergency}
                  onClick={() =>
                    ask({
                      type: 'start_lesson',
                      lesson: lessonInput,
                      title: `بدء الحصة ${lessonInput}`,
                      message: `يقفز إلى الحصة ${lessonInput}: إعلان بدايتها ثم جرسها ثم عدادها. مفيد بعد انقطاع الكهرباء في منتصف الدوام.`,
                    })
                  }
                >
                  ابدأ من هذه الحصة
                </button>
              </div>
            </div>

            <div>
              <h3 style={{ marginTop: 0 }}>الصوت</h3>
              <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                <button
                  disabled={!canPlay || emergency}
                  onClick={() =>
                    ask({
                      type: 'play_anthem',
                      title: 'تشغيل النشيد الكامل',
                      message: 'يعادل زر النشيد: يشغّل 0003 ولا يقاطع إعلانًا مدرسيًا جاريًا.',
                    })
                  }
                >
                  النشيد الكامل
                </button>
                <button
                  disabled={!canPlay}
                  onClick={() =>
                    ask({
                      type: 'stop_audio',
                      title: 'إيقاف التشغيل اليدوي',
                      message: 'يوقف النشيد الكامل أو المقطع اليدوي فقط، ولا يوقف إعلانات الجدول أو الطوارئ.',
                    })
                  }
                >
                  إيقاف النشيد/المقطع
                </button>
              </div>
              <div className="row" style={{ marginTop: 10, gap: 8 }}>
                <input
                  type="number"
                  min={1}
                  max={255}
                  aria-label="رقم المقطع"
                  value={trackInput}
                  onChange={(e) => setTrackInput(Math.max(1, Math.min(255, Number(e.target.value) || 1)))}
                  style={{ width: 90 }}
                />
                <button
                  disabled={!canPlay || emergency}
                  onClick={() =>
                    ask({
                      type: 'play_track',
                      track: trackInput,
                      title: `تشغيل المقطع ${String(trackInput).padStart(4, '0')}.mp3`,
                      message: 'يشغّل ملفًا من مجلد mp3 على بطاقة الذاكرة. لا يقاطع إعلانًا مدرسيًا جاريًا.',
                    })
                  }
                >
                  تشغيل مقطع
                </button>
              </div>
              <div className="row" style={{ marginTop: 10, gap: 8 }}>
                <input
                  type="range"
                  min={0}
                  max={30}
                  aria-label="مستوى الصوت"
                  value={volumeInput ?? s?.volume ?? data.config.volume}
                  onChange={(e) => setVolumeInput(Number(e.target.value))}
                  style={{ width: 160 }}
                />
                <span className="num">{volumeInput ?? s?.volume ?? data.config.volume}</span>
                <button
                  disabled={!canPlay || volumeInput === null}
                  onClick={() =>
                    ask({
                      type: 'set_volume',
                      volume: volumeInput ?? 0,
                      title: 'ضبط مستوى الصوت',
                      message: `يضبط الصوت إلى ${volumeInput} مؤقتًا حتى إعادة التشغيل. للتثبيت عدّل الإعدادات أدناه.`,
                    })
                  }
                >
                  تطبيق
                </button>
              </div>
            </div>

            <div>
              <h3>الطوارئ</h3>
              <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                <button
                  className="danger"
                  disabled={!can('incident.activate') || Boolean(s?.emergency)}
                  onClick={() =>
                    ask({
                      type: 'start_emergency',
                      danger: true,
                      title: 'تفعيل طوارئ هذا الجهاز',
                      message:
                        'يعادل زر الطوارئ على هذا الجهاز وحده: صفارة وإعلان متكرر (0018/0019). لإنذار مدرسة كاملة استخدم شاشة الطوارئ.',
                    })
                  }
                >
                  تفعيل الطوارئ
                </button>
                <button
                  disabled={!can('incident.clear') || !emergency}
                  onClick={() =>
                    ask({
                      type: 'cancel_emergency',
                      title: 'إلغاء الطوارئ على هذا الجهاز',
                      message:
                        'يعادل الضغط المطوّل على SKIP: يوقف الصفارة ويشغّل تسلسل انتهاء الطوارئ (0020 ثم 0021 مرتين) ثم يعود للجدول. إن كانت الطوارئ من حادثة في المنصة فلن يعيد تشغيلها.',
                    })
                  }
                >
                  إلغاء الطوارئ
                </button>
              </div>
              {!can('incident.activate') || !can('incident.clear') ? (
                <div className="hint" style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 6 }}>
                  أزرار الطوارئ تتطلب تفويض «تفعيل الإنذار» و«إلغاء الإنذار» من صفحة المستخدمين.
                </div>
              ) : null}
            </div>

            <div>
              <h3>الجهاز</h3>
              <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                <button
                  disabled={!canManage}
                  onClick={() =>
                    ask({
                      type: 'sync_time',
                      title: 'مزامنة الساعة',
                      message: 'يضبط ساعة RTC في الجهاز على وقت المنصة الآن.',
                    })
                  }
                >
                  مزامنة الساعة
                </button>
                <button
                  disabled={!canManage}
                  onClick={() =>
                    ask({
                      type: 'reboot',
                      title: 'إعادة تشغيل الجهاز',
                      message:
                        'يعيد تشغيل ESP32 (نحو 10 ثوانٍ). إن كان «تشغيل الجدول تلقائيًا» مفعّلًا ينضم الجهاز إلى الحصة الجارية بصمت بعد الإقلاع؛ وإلا يتوقف اليوم الجاري.',
                      danger: true,
                    })
                  }
                >
                  إعادة التشغيل
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>

      <div className="grid cols-2">
        <Card title="حالة الجهاز">
          {!s ? (
            <Empty title="لم تصل حالة بعد" hint="تظهر بعد أول مزامنة من الجهاز." />
          ) : (
            <table>
              <tbody>
                <Row label="التسلسل الصوتي">{SEQUENCE_LABELS[s.sequence ?? 'none'] ?? s.sequence}</Row>
                <Row label="الحصة">
                  <span className="num">
                    {s.lesson ?? 0} / {lessonCount}
                  </span>
                </Row>
                <Row label="المقطع الحالي">
                  {s.audio_playing ? (
                    <>
                      <span className="mono">{String(s.track ?? 0).padStart(4, '0')}.mp3</span> —{' '}
                      {OWNER_LABELS[s.owner ?? 'none']}
                    </>
                  ) : (
                    '—'
                  )}
                </Row>
                <Row label="مستوى الصوت">
                  <span className="num">{s.volume ?? '—'}</span> / 30
                </Row>
                <Row label="ساعة الجهاز">
                  <span className="mono">{s.rtc_local ?? '—'}</span>{' '}
                  <Badge tone={clock.tone} icon={clock.icon}>
                    {clock.label}
                  </Badge>
                </Row>
                <Row label="وحدة RTC">
                  {s.rtc_ok === false ? (
                    <Badge tone="danger" icon="✖">
                      غير موجودة
                    </Badge>
                  ) : (
                    <Badge tone="ok" icon="✔">
                      سليمة
                    </Badge>
                  )}
                </Row>
                <Row label="مشغل MP3">
                  {s.player_ok === false ? (
                    <Badge tone="danger" icon="✖">
                      غير متصل
                    </Badge>
                  ) : (
                    <Badge tone="ok" icon="✔">
                      سليم
                    </Badge>
                  )}
                </Row>
                <Row label="الشبكة">
                  {s.ssid ? <span>{s.ssid}</span> : '—'}
                  {s.wifi_rssi !== undefined ? (
                    <span className="num" style={{ color: 'var(--muted)' }}>
                      {' '}
                      ({s.wifi_rssi} dBm)
                    </span>
                  ) : null}
                  {s.ip ? <span className="mono"> · {s.ip}</span> : null}
                </Row>
                {s.ip ? (
                  <Row label="شاشة الجهاز المحلية">
                    <a href={`http://${s.ip}/`} target="_blank" rel="noreferrer" className="mono">
                      http://{s.ip}/
                    </a>
                    <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
                      {' '}· تعمل داخل شبكة المدرسة بدون إنترنت (إصدار 4.2.0 فأحدث)
                    </span>
                  </Row>
                ) : null}
                <Row label="مدة التشغيل">{formatUptime(s.uptime_ms)}</Row>
                <Row label="الإصدار">
                  <span className="mono">{data.firmware_version ?? '—'}</span>
                </Row>
                <Row label="الذاكرة الداخلية">
                  {s.flash_kb ? (
                    <span style={{ fontSize: 13 }}>
                      فلاش <span className="num">{(s.flash_kb / 1024).toFixed(0)}</span> م.ب · البرنامج{' '}
                      <span className="num">{((s.app_kb ?? 0) / 1024).toFixed(2)}</span> م.ب · متاح للتحديث{' '}
                      <span className="num">{((s.app_free_kb ?? 0) / 1024).toFixed(2)}</span> م.ب
                    </span>
                  ) : (
                    '—'
                  )}
                </Row>
                {s.sd_total_mb ? (
                  <Row label="بطاقة الذاكرة">
                    <span style={{ fontSize: 13 }}>
                      <span className="num">{s.sd_used_mb ?? 0}</span> م.ب مستخدمة من{' '}
                      <span className="num">{s.sd_total_mb}</span> م.ب · عليها{' '}
                      <span className="num">{s.card_tracks?.length ?? 0}</span> مقطعًا
                    </span>
                  </Row>
                ) : null}
                {s.events_dropped ? (
                  <Row label="أحداث فُقدت محليًا">
                    <Badge tone="warn" icon="▲">
                      {s.events_dropped}
                    </Badge>
                  </Row>
                ) : null}
              </tbody>
            </table>
          )}
          <Notice tone="info">
            «بدأ التشغيل» يعني أن الجهاز أرسل الأمر لمشغل MP3 وشغّل المضخم. لا يوجد حساس صوت يؤكد أن الصوت
            سُمع في المدرسة.
          </Notice>
        </Card>

        <BellConfigEditor
          deviceId={device.id}
          config={data.config}
          updatedAt={data.config_updated_at}
          holidays={data.effective_config.holidays}
          applied={data.config_applied}
          canEdit={canManage}
          timezone={tz}
          schedule={data.schedule}
          sounds={data.sound_set.entries}
        />
      </div>

      <SoundSetCard
        set={data.sound_set}
        state={s}
        canManage={canManage && active}
        ask={ask}
        deviceId={device.id}
      />

      <NetworkFirmwareCard
        state={s}
        firmwareVersion={data.firmware_version}
        canManage={canManage && active}
        canFirmware={can('firmware.manage') && active}
        ask={ask}
      />

      <Card title="آخر الأوامر" subtitle="«أُرسل» لا يساوي «نُفّذ»">
        {commands.isLoading ? (
          <Loading />
        ) : (commands.data?.items.length ?? 0) === 0 ? (
          <Empty title="لا توجد أوامر" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الأمر</th>
                  <th>وقت الإصدار</th>
                  <th>التنفيذ</th>
                  <th>النتيجة</th>
                  <th>السبب</th>
                </tr>
              </thead>
              <tbody>
                {commands.data!.items.map((c) => {
                  const ack = c.ack_stage ? ACK_STAGE_LABELS[c.ack_stage] : null;
                  return (
                    <tr key={c.command_id}>
                      <td>{commandLabel(c)}</td>
                      <td style={{ fontSize: 12.5 }}>{formatDateTime(c.issued_at, tz)}</td>
                      <td>
                        {ack ? (
                          <Badge tone={ack.tone}>{ack.label}</Badge>
                        ) : (
                          <Badge tone="muted" icon="…">
                            بانتظار الجهاز
                          </Badge>
                        )}
                      </td>
                      <td style={{ fontSize: 12.5 }}>
                        {c.ack_code ? (BELL_ACK_CODE_LABELS[c.ack_code] ?? c.ack_code) : '—'}
                        {c.ack_detail ? (
                          <div style={{ color: 'var(--muted)' }}>{c.ack_detail}</div>
                        ) : null}
                      </td>
                      <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{c.reason ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="سجل أحداث الجهاز" subtitle="الأزرار، التسلسل الصوتي، الطوارئ، الأعطال">
        {device.recent_events.length === 0 ? (
          <Empty title="لا توجد أحداث مسجلة" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الحدث</th>
                  <th>التفاصيل</th>
                  <th>الوقت</th>
                </tr>
              </thead>
              <tbody>
                {device.recent_events.map((e, i) => (
                  <tr key={i}>
                    <td>{DEVICE_EVENT_LABELS[e.type] ?? e.type}</td>
                    <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{describeDetails(e.details)}</td>
                    <td style={{ fontSize: 12.5 }}>{formatDateTime(e.occurred_at, tz)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {pending ? (
        <ConfirmDialog
          title={pending.title}
          danger={pending.danger ?? false}
          busy={send.isPending}
          confirmLabel="إرسال الأمر"
          message={pending.message}
          onCancel={() => setPending(null)}
          onConfirm={() => send.mutate(pending)}
        >
          <div className="field" style={{ marginTop: 12 }}>
            <label>السبب (اختياري، يُسجَّل في التدقيق)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />
          </div>
        </ConfirmDialog>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------- network and firmware */

const OTA_STAGE_LABELS: Record<string, { label: string; tone: string }> = {
  downloading: { label: 'تنزيل البرنامج', tone: 'info' },
  verifying: { label: 'التحقق من البرنامج', tone: 'info' },
  rebooting: { label: 'إعادة التشغيل على الإصدار الجديد', tone: 'info' },
  failed: { label: 'فشل التحديث (يعمل الإصدار الحالي)', tone: 'danger' },
};

function NetworkFirmwareCard({
  state,
  firmwareVersion,
  canManage,
  canFirmware,
  ask,
}: {
  state: BellState | null;
  firmwareVersion: string | null;
  canManage: boolean;
  canFirmware: boolean;
  ask: (cmd: PendingCommand) => void;
}): JSX.Element {
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [releaseId, setReleaseId] = useState('');

  const releases = useQuery({
    queryKey: ['firmware'],
    queryFn: () => get<{ releases: Array<{ id: string; version: string; bytes: number }> }>('/firmware'),
    enabled: canFirmware,
  });

  const ota = state?.ota_stage && state.ota_stage !== 'idle' ? OTA_STAGE_LABELS[state.ota_stage] : null;
  const passwordInvalid = password.length > 0 && password.length < 8;

  return (
    <Card title="الشبكة وبرنامج الجهاز">
      <div className="grid cols-2">
        <div>
          <h3 style={{ marginTop: 0 }}>شبكة WiFi</h3>
          <table>
            <tbody>
              <Row label="الشبكة الحالية">
                {state?.ssid ?? '—'}{' '}
                {state?.wifi_slot === 'fallback' ? (
                  <Badge tone="warn" icon="▲">
                    الشبكة الاحتياطية
                  </Badge>
                ) : null}
              </Row>
              <Row label="الإشارة">
                <span className="num">{state?.wifi_rssi ?? '—'}</span> dBm
              </Row>
              <Row label="العنوان">
                <span className="mono">{state?.ip ?? '—'}</span>
              </Row>
            </tbody>
          </table>

          {canManage ? (
            <>
              <div className="row" style={{ gap: 8, margin: '10px 0' }}>
                <button
                  onClick={() =>
                    ask({
                      type: 'scan_wifi',
                      title: 'البحث عن الشبكات',
                      message: 'يبحث الجهاز عن الشبكات القريبة (بضع ثوانٍ) وتظهر النتائج هنا.',
                    })
                  }
                >
                  البحث عن الشبكات
                </button>
              </div>
              {(state?.wifi_scan ?? []).length > 0 ? (
                <div className="pill-list" style={{ marginBottom: 10 }}>
                  {state!.wifi_scan!.map((n) => (
                    <button key={n.ssid} type="button" className="ghost" onClick={() => setSsid(n.ssid)}>
                      {n.ssid} <span className="num">({n.rssi})</span>
                    </button>
                  ))}
                </div>
              ) : null}
              <Field label="اسم الشبكة الجديدة">
                <input value={ssid} onChange={(e) => setSsid(e.target.value)} maxLength={32} />
              </Field>
              <Field
                label="كلمة المرور"
                hint="فارغة للشبكة المفتوحة"
                error={passwordInvalid ? '8 أحرف على الأقل' : undefined}
              >
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  maxLength={63}
                />
              </Field>
              <button
                disabled={!ssid.trim() || passwordInvalid}
                onClick={() =>
                  ask({
                    type: 'set_wifi',
                    ssid: ssid.trim(),
                    password,
                    title: `تغيير الشبكة إلى «${ssid.trim()}»`,
                    message:
                      'يجرّب الجهاز الشبكة الجديدة. إن لم يصل منها إلى المنصة خلال دقيقة يعود تلقائيًا للشبكة الحالية ويبقى متصلًا. الجرس لا يتوقف أثناء ذلك. كلمة المرور لا تُحفظ في سجلات المنصة.',
                  })
                }
              >
                تغيير الشبكة
              </button>
            </>
          ) : null}
        </div>

        <div>
          <h3 style={{ marginTop: 0 }}>برنامج الجهاز</h3>
          <table>
            <tbody>
              <Row label="الإصدار الحالي">
                <span className="mono">{firmwareVersion ?? '—'}</span>
              </Row>
              {ota ? (
                <Row label="التحديث">
                  <Badge tone={ota.tone}>{ota.label}</Badge>{' '}
                  {state?.ota_stage === 'downloading' ? (
                    <span className="num">{state.ota_progress ?? 0}%</span>
                  ) : null}
                  {state?.ota_version ? <span className="mono"> → {state.ota_version}</span> : null}
                </Row>
              ) : null}
            </tbody>
          </table>

          {canFirmware ? (
            (releases.data?.releases.length ?? 0) === 0 ? (
              <Notice tone="info">
                لا توجد إصدارات مرفوعة. ارفع ملف البرنامج من صفحة <Link to="/firmware">تحديث برنامج الأجهزة</Link>.
              </Notice>
            ) : (
              <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <select value={releaseId} onChange={(e) => setReleaseId(e.target.value)} style={{ width: 'auto' }}>
                  <option value="">اختر الإصدار</option>
                  {releases.data!.releases.map((r) => (
                    <option key={r.id} value={r.id} disabled={r.version === firmwareVersion}>
                      {r.version}
                      {r.version === firmwareVersion ? ' (الحالي)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  className="primary"
                  disabled={!releaseId}
                  onClick={() => {
                    const r = releases.data!.releases.find((x) => x.id === releaseId);
                    ask({
                      type: 'ota_update',
                      firmware_id: releaseId,
                      title: `تحديث البرنامج إلى ${r?.version ?? ''}`,
                      message:
                        'ينزّل الجهاز البرنامج ويتحقق منه ثم يعيد التشغيل (نحو دقيقة، يتوقف الجرس خلالها). إن لم يتصل الإصدار الجديد بالمنصة يعود تلقائيًا للإصدار السابق. يُفضّل خارج وقت الدوام.',
                      danger: true,
                    });
                  }}
                >
                  تحديث
                </button>
              </div>
            )
          ) : null}
        </div>
      </div>
    </Card>
  );
}

/* --------------------------------------------------------------- sound set */

function trackCode(track: number): string {
  return String(track).padStart(4, '0');
}

function SoundSetCard({
  set,
  state,
  canManage,
  ask,
  deviceId,
}: {
  set: SoundSetView;
  state: BellState | null;
  canManage: boolean;
  ask: (cmd: PendingCommand) => void;
  deviceId: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [slot, setSlot] = useState<number | null>(null);
  const [note, setNote] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);

  const missing = new Set(state?.missing_tracks ?? []);
  const reported = state?.card_tracks ?? null;
  const onCard = new Set(reported ?? []);
  const synced = set.synced_card;
  // On the card but not in the library any more: the device drops these when the set is complete.
  const extras = (reported ?? []).filter((t) => !set.entries.some((e) => e.track === t));

  // Uploading with a track number replaces whatever holds that number, everywhere.
  const replace = useMutation({
    mutationFn: ({ track, file }: { track: number; file: File }) => {
      const form = new FormData();
      form.append('track', String(track));
      form.append('file', file);
      return api<{ id: string }>('/audio', { method: 'POST', body: form });
    },
    onSuccess: (_res, vars) => {
      setNote({
        tone: 'info',
        text: `رُفع الملف للمقطع ${trackCode(vars.track)}. يُجهَّز خلال ثوانٍ${synced ? ' ثم ينزل إلى بطاقة الجهاز' : '، ثم نزّله للبطاقة وانسخه يدويًا'}.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['bell-live', deviceId] });
      void queryClient.invalidateQueries({ queryKey: ['audio'] });
    },
    onError: (e: Error) => setNote({ tone: 'danger', text: e.message }),
  });

  const pick = (track: number): void => {
    setSlot(track);
    setNote(null);
    fileInput.current?.click();
  };

  const askDelete = (track: number): void =>
    ask({
      type: 'delete_track',
      track,
      title: `حذف المقطع ${trackCode(track)} من البطاقة`,
      message:
        'يُحذف الملف من بطاقة الجهاز فورًا. إن كان الرقم ما زال مستخدمًا في مكتبة الأصوات فسيُنزَّل من جديد (إصلاح ملف تالف). لإزالته نهائيًا احذف رقمه من المكتبة أولًا.',
      danger: true,
    });

  const status = (track: number): JSX.Element => {
    if (!synced) return <span style={{ color: 'var(--muted)' }}>—</span>;
    if (state?.downloading_track === track) {
      return (
        <Badge tone="info" icon="↓">
          قيد التنزيل
        </Badge>
      );
    }
    const here = reported ? onCard.has(track) && !missing.has(track) : set.applied && !missing.has(track);
    if (here) {
      return (
        <Badge tone="ok" icon="✔">
          على البطاقة
        </Badge>
      );
    }
    return (
      <Badge tone="warn" icon="…">
        بانتظار التنزيل
      </Badge>
    );
  };

  return (
    <Card
      title="أصوات الجهاز"
      subtitle={
        synced
          ? set.applied
            ? 'كل الأصوات منزّلة على بطاقة الجهاز وتعمل بدون إنترنت'
            : `مزامنة جارية: ${state?.assets_ready ?? 0} من ${state?.assets_total ?? set.entries.length}`
          : 'مرجع لمحتوى بطاقة وحدة MP3'
      }
      actions={
        synced && canManage ? (
          <button
            onClick={() =>
              ask({
                type: 'sync_assets',
                title: 'مزامنة إجبارية لأصوات البطاقة',
                message:
                  'يفحص الجهاز كل ملف على بطاقته، ويحذف الناقص أو التالف، ثم يطلب القائمة من جديد وينزّل ما يلزم فورًا. الجرس والطوارئ تعمل أثناء ذلك.',
              })
            }
          >
            مزامنة إجبارية
          </button>
        ) : null
      }
    >
      {note ? <Notice tone={note.tone}>{note.text}</Notice> : null}
      <input
        ref={fileInput}
        type="file"
        accept=".wav,.mp3,.m4a,audio/wav,audio/mpeg,audio/mp4"
        className="sr-only"
        aria-label="اختيار ملف بديل"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file && slot !== null) replace.mutate({ track: slot, file });
        }}
      />

      {synced ? (
        state?.sd_total_mb ? (
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
            بطاقة الذاكرة: <span className="num">{state.sd_used_mb ?? 0}</span> م.ب مستخدمة من{' '}
            <span className="num">{state.sd_total_mb}</span> م.ب
            {reported ? (
              <>
                {' '}
                · على البطاقة الآن <span className="num">{reported.length}</span> مقطعًا
              </>
            ) : null}
          </div>
        ) : null
      ) : (
        <Notice tone="info">
          هذا الجهاز بوحدة MP3: يشغّل الأصوات من بطاقة الوحدة نفسها، ولا يستطيع الـ ESP32 الكتابة عليها. انسخ
          الملفات يدويًا بنفس الأرقام، أو ركّب قارئ بطاقة microSD مع مخرج I2S لتصبح المزامنة تلقائية.
        </Notice>
      )}

      {set.entries.length === 0 ? (
        <Empty
          title="لا توجد أصوات مرقّمة في المكتبة"
          hint="حدّد «رقم الجرس» للملفات في مكتبة الأصوات، عند الرفع أو من جدول المكتبة."
        />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>المقطع</th>
                <th>الملف في المكتبة</th>
                <th>المدة</th>
                <th>الحجم</th>
                <th>على الجهاز</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {set.entries.map((e) => (
                <tr key={e.track}>
                  <td className="num">{trackCode(e.track)}</td>
                  <td>{e.name}</td>
                  <td className="num">{formatDuration(e.duration_ms)}</td>
                  <td style={{ fontSize: 12.5 }}>{formatBytes(e.bytes)}</td>
                  <td>{status(e.track)}</td>
                  <td className="actions">
                    {canManage ? (
                      <button className="ghost" disabled={replace.isPending} onClick={() => pick(e.track)}>
                        استبدال
                      </button>
                    ) : null}
                    {synced && canManage ? (
                      <button className="ghost" onClick={() => askDelete(e.track)}>
                        حذف من البطاقة
                      </button>
                    ) : null}
                    {!synced ? (
                      <a href={`/api/v1/audio/${e.asset_id}/card-file`} download style={{ whiteSpace: 'nowrap' }}>
                        تنزيل للبطاقة
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {extras.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>ملفات على البطاقة ليست في المكتبة</div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {extras.map((t) => (
              <span key={t} className="row" style={{ gap: 4 }}>
                <Badge tone="muted">{trackCode(t)}</Badge>
                {canManage ? (
                  <button className="ghost" onClick={() => askDelete(t)}>
                    حذف
                  </button>
                ) : null}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>
            يحذفها الجهاز وحده عند اكتمال المزامنة، أو احذفها الآن.
          </div>
        </div>
      ) : null}

      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 8 }}>
        «استبدال» يرفع ملفًا جديدًا بنفس الرقم فيحل محل القديم على كل الأجهزة. أو ارفعه من{' '}
        <Link to="/audio">مكتبة الأصوات</Link> واختر رقمه من «رقم الجرس»
        {synced ? ' فيصل إلى الجهاز تلقائيًا' : '، ثم «تنزيل للبطاقة» وانسخه إلى مجلد mp3 في بطاقة وحدة MP3'}.
      </div>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <tr>
      <th>{label}</th>
      <td>{children}</td>
    </tr>
  );
}

/* ---------------------------------------------------------- template view */

const ASSIGNMENT_SOURCE_LABELS: Record<string, string> = {
  school: 'المدرسة أو الجهاز',
  group: 'مجموعة المدارس',
  tenant: 'المديرية',
  school_date_exception: 'استثناء بتاريخ',
};

function minutesToTime(totalMinutes: number): string {
  const m = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

function shiftEnd(s: WireShift): string {
  const seconds = s.l.reduce((a, v) => a + v, 0) + s.b.reduce((a, v) => a + v, 0);
  return minutesToTime(s.t + seconds / 60);
}

function TemplateScheduleView({ schedule, pending }: { schedule: ScheduleView; pending: boolean }): JSX.Element {
  if (pending) {
    return <Notice tone="info">احفظ لعرض الجدول المُسند إلى هذا الجهاز وإرساله إليه.</Notice>;
  }
  return (
    <>
      {schedule.template ? (
        <div style={{ margin: '4px 0 8px' }}>
          الجدول المطبّق: <b>{schedule.template.name}</b>{' '}
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>
            (مُسند إلى {ASSIGNMENT_SOURCE_LABELS[schedule.template.assignment_source] ?? schedule.template.assignment_source})
          </span>{' '}
          — <Link to={`/schedules/${schedule.template.id}`}>تعديل الجدول</Link>
        </div>
      ) : null}

      {schedule.problems.length > 0 ? (
        <Notice tone={schedule.template ? 'warn' : 'danger'}>
          <ul style={{ margin: 0, paddingInlineStart: 18 }}>
            {schedule.problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
          {!schedule.template ? (
            <div style={{ marginTop: 6 }}>
              أسند جدولًا للمدرسة من <Link to="/schedules">الجداول</Link>.
            </div>
          ) : null}
        </Notice>
      ) : null}

      {schedule.template ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>اليوم</th>
                <th>الدوام</th>
              </tr>
            </thead>
            <tbody>
              {WEEKDAYS.map((w) => {
                const shifts = schedule.week[w.value] ?? [];
                return (
                  <tr key={w.value}>
                    <td>{w.label}</td>
                    <td style={{ fontSize: 13 }}>
                      {shifts.length === 0 ? (
                        <span style={{ color: 'var(--muted)' }}>لا دوام</span>
                      ) : (
                        shifts.map((s, i) => (
                          <div key={i}>
                            <span className="num">
                              {minutesToTime(s.t)} – {shiftEnd(s)}
                            </span>{' '}
                            <span style={{ color: 'var(--muted)' }}>
                              ({s.l.length} حصص: {s.l.map((v) => Math.round(v / 60)).join('، ')} د)
                            </span>
                          </div>
                        ))
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {schedule.overrides.length > 0 ? (
        <>
          <div style={{ fontWeight: 600, marginTop: 10 }}>أيام مختلفة خلال الستين يومًا القادمة</div>
          <div className="pill-list" style={{ marginTop: 6 }}>
            {schedule.overrides.slice(0, 12).map((o) => (
              <Badge key={o.date} tone={o.s.length === 0 ? 'muted' : 'info'}>
                <span className="num">{formatHoliday(o.date)}</span>{' '}
                {o.s.length === 0 ? 'لا دوام' : 'جدول خاص'}
                {o.label ? ` — ${o.label}` : ''}
              </Badge>
            ))}
          </div>
        </>
      ) : null}
      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 8 }}>
        يُحفظ الجدول في الجهاز ويعمل بدون إنترنت. أي تعديل في «الجداول» أو «الاستثناءات والعطل» يصل إلى الجهاز
        خلال ثوانٍ. جهاز الجرس يعلن حتى 6 حصص بأصواته المرقّمة.
      </div>
    </>
  );
}

/* ------------------------------------------------------------ config editor */

type Unit = 'min' | 'sec';

function BellConfigEditor({
  deviceId,
  config,
  updatedAt,
  holidays,
  applied,
  canEdit,
  timezone,
  schedule,
  sounds,
}: {
  deviceId: string;
  config: BellConfig;
  updatedAt: string | null;
  holidays: number[];
  applied: boolean;
  canEdit: boolean;
  timezone: string;
  schedule: ScheduleView;
  sounds: SoundSetView['entries'];
}): JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BellConfig>(config);
  const [dirty, setDirty] = useState(false);
  const [unit, setUnit] = useState<Unit>(() =>
    config.lesson_seconds.every((v) => v % 60 === 0) && config.break_seconds.every((v) => v % 60 === 0)
      ? 'min'
      : 'sec',
  );
  const [message, setMessage] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);

  // Follow the server copy until the operator starts editing.
  const serverKey = useMemo(() => JSON.stringify(config), [config]);
  useEffect(() => {
    if (!dirty) setDraft(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  const save = useMutation({
    mutationFn: (body: BellConfig) => put<{ note: string; warning?: string }>(`/devices/${deviceId}/bell/config`, body),
    onSuccess: (res) => {
      setDirty(false);
      setMessage(res.warning ? { tone: 'danger', text: `${res.note}. ${res.warning}` } : { tone: 'info', text: res.note });
      void queryClient.invalidateQueries({ queryKey: ['bell-live', deviceId] });
    },
    onError: (e: Error) => setMessage({ tone: 'danger', text: e.message }),
  });

  const update = (fn: (c: BellConfig) => BellConfig): void => {
    setDirty(true);
    setMessage(null);
    setDraft((c) => fn(c));
  };

  const toUnit = (seconds: number): number => (unit === 'min' ? Math.round((seconds / 60) * 100) / 100 : seconds);
  const fromUnit = (value: number): number => Math.round(unit === 'min' ? value * 60 : value);

  const setDuration = (key: 'lesson_seconds' | 'break_seconds', index: number, value: number): void =>
    update((c) => {
      const next = [...c[key]];
      next[index] = fromUnit(value);
      return { ...c, [key]: next };
    });

  const setAll = (lesson: number, brk: number): void =>
    update((c) => ({ ...c, lesson_seconds: c.lesson_seconds.map(() => lesson), break_seconds: c.break_seconds.map(() => brk) }));

  return (
    <Card
      title="إعدادات الجرس"
      subtitle={updatedAt ? `آخر تعديل ${formatDateTime(updatedAt, timezone)}` : 'الإعدادات الافتراضية'}
    >
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
      {!dirty ? (
        applied ? (
          <Badge tone="ok" icon="✔">
            محفوظة في الجهاز وتعمل بدون إنترنت
          </Badge>
        ) : (
          <Badge tone="warn" icon="…">
            بانتظار وصولها إلى الجهاز
          </Badge>
        )
      ) : (
        <Badge tone="info" icon="✎">
          تعديلات غير محفوظة
        </Badge>
      )}

      <fieldset disabled={!canEdit || save.isPending} style={{ border: 0, padding: 0, margin: '12px 0 0' }}>
        <label className="row" style={{ gap: 8, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={draft.auto_start_enabled}
            onChange={(e) => update((c) => ({ ...c, auto_start_enabled: e.target.checked }))}
            style={{ width: 'auto' }}
          />
          تشغيل الجدول تلقائيًا (من ساعة الجهاز، ويعمل بدون إنترنت)
        </label>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', margin: '2px 0 10px' }}>
          إلغاؤه يوقف البدء التلقائي على الجهاز خلال ثوانٍ؛ الأزرار والأوامر اليدوية تبقى تعمل.
        </div>

        <div className="grid cols-3">
          <Field label="مصدر الجدول">
            <select
              value={draft.source}
              onChange={(e) => update((c) => ({ ...c, source: e.target.value as BellConfig['source'] }))}
            >
              <option value="template">من صفحة «الجداول» (المُسند للمدرسة أو الجهاز)</option>
              <option value="manual">إعدادات يدوية لهذا الجهاز</option>
            </select>
          </Field>
          <Field
            label="التوقيت"
            hint={
              draft.timing === 'clock'
                ? 'كل حصة تنتهي في وقتها المحدد مهما طالت الإعلانات'
                : 'العداد يبدأ بعد انتهاء جرس بداية الحصة، كالكود الأصلي'
            }
          >
            <select
              value={draft.timing}
              onChange={(e) => update((c) => ({ ...c, timing: e.target.value as BellConfig['timing'] }))}
            >
              <option value="clock">مطابق للساعة</option>
              <option value="after_bell">بعد انتهاء الجرس</option>
            </select>
          </Field>
          <Field label={`مستوى الصوت (${draft.volume})`}>
            <input
              type="range"
              min={0}
              max={30}
              value={draft.volume}
              onChange={(e) => update((c) => ({ ...c, volume: Number(e.target.value) }))}
            />
          </Field>
        </div>

        {draft.source === 'template' ? (
          <TemplateScheduleView schedule={schedule} pending={dirty && config.source !== 'template'} />
        ) : (
          <>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <button type="button" className="ghost" onClick={() => setAll(15, 10)}>
            قيم الاختبار (15ث / 10ث)
          </button>
          <button type="button" className="ghost" onClick={() => setAll(45 * 60, 5 * 60)}>
            دوام فعلي (45د / 5د)
          </button>
        </div>

        <div className="grid cols-3">
          <Field label="عدد الحصص">
            <select
              value={draft.lesson_count}
              onChange={(e) => update((c) => ({ ...c, lesson_count: Number(e.target.value) }))}
            >
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </Field>
          <Field label="وحدة المدد">
            <select value={unit} onChange={(e) => setUnit(e.target.value as Unit)}>
              <option value="min">دقائق</option>
              <option value="sec">ثوانٍ</option>
            </select>
          </Field>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>الحصة</th>
                <th>مدة الحصة ({unit === 'min' ? 'دقيقة' : 'ثانية'})</th>
                <th>الاستراحة بعدها</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: draft.lesson_count }, (_, i) => (
                <tr key={i}>
                  <td className="num">{i + 1}</td>
                  <td>
                    <input
                      type="number"
                      min={unit === 'min' ? 0.17 : 10}
                      step={unit === 'min' ? 0.5 : 1}
                      value={toUnit(draft.lesson_seconds[i] ?? 0)}
                      onChange={(e) => setDuration('lesson_seconds', i, Number(e.target.value))}
                      style={{ width: 110 }}
                    />
                  </td>
                  <td>
                    {i < draft.lesson_count - 1 ? (
                      <input
                        type="number"
                        min={unit === 'min' ? 0.09 : 5}
                        step={unit === 'min' ? 0.5 : 1}
                        value={toUnit(draft.break_seconds[i] ?? 0)}
                        onChange={(e) => setDuration('break_seconds', i, Number(e.target.value))}
                        style={{ width: 110 }}
                      />
                    ) : (
                      <span style={{ color: 'var(--muted)' }}>نهاية اليوم</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', margin: '6px 0 12px' }}>
          أقل مدة للحصة 10 ثوانٍ وللاستراحة 5 ثوانٍ.
        </div>

        <div style={{ fontWeight: 600 }}>أوقات بدء اليوم</div>

        {draft.starts.map((start, idx) => (
          <div key={idx} className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <input
              type="time"
              value={start.time}
              onChange={(e) =>
                update((c) => ({
                  ...c,
                  starts: c.starts.map((st, j) => (j === idx ? { ...st, time: e.target.value } : st)),
                }))
              }
              style={{ width: 120 }}
            />
            {WEEKDAYS.map((w) => (
              <label key={w.value} className="row" style={{ gap: 3, fontSize: 13 }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={start.days.includes(w.value)}
                  onChange={(e) =>
                    update((c) => ({
                      ...c,
                      starts: c.starts.map((st, j) =>
                        j === idx
                          ? {
                              ...st,
                              days: e.target.checked
                                ? [...st.days, w.value].sort()
                                : st.days.filter((d) => d !== w.value),
                            }
                          : st,
                      ),
                    }))
                  }
                />
                {w.label}
              </label>
            ))}
            <button
              type="button"
              className="ghost"
              onClick={() => update((c) => ({ ...c, starts: c.starts.filter((_, j) => j !== idx) }))}
            >
              حذف
            </button>
          </div>
        ))}
        {draft.starts.length < 4 ? (
          <button
            type="button"
            className="ghost"
            style={{ marginTop: 8 }}
            onClick={() =>
              update((c) => ({ ...c, starts: [...c.starts, { time: '12:30', days: [0, 1, 2, 3, 4] }] }))
            }
          >
            + وقت بدء آخر (دوام ثانٍ)
          </button>
        ) : null}

        <label className="row" style={{ gap: 8, marginTop: 12 }}>
          <input
            type="checkbox"
            checked={draft.skip_holidays}
            onChange={(e) => update((c) => ({ ...c, skip_holidays: e.target.checked }))}
            style={{ width: 'auto' }}
          />
          لا تبدأ تلقائيًا في العطل المسجلة في <Link to="/calendar">الاستثناءات والعطل</Link>
        </label>
        {draft.skip_holidays && holidays.length > 0 ? (
          <div className="pill-list" style={{ marginTop: 6 }}>
            {holidays.slice(0, 10).map((h) => (
              <Badge key={h} tone="muted">
                <span className="num">{formatHoliday(h)}</span>
              </Badge>
            ))}
          </div>
        ) : null}

          </>
        )}

        <ScenarioEditor
          value={draft.scenario ?? DEFAULT_SCENARIO}
          onChange={(scenario) => update((c) => ({ ...c, scenario }))}
          sounds={sounds}
        />

        <div className="row end" style={{ marginTop: 14, gap: 8 }}>
          {dirty ? (
            <button
              type="button"
              onClick={() => {
                setDraft(config);
                setDirty(false);
              }}
            >
              تراجع
            </button>
          ) : null}
          <button type="button" className="primary" disabled={!dirty} onClick={() => save.mutate(draft)}>
            {save.isPending ? 'جارٍ الحفظ…' : 'حفظ وإرسال للجهاز'}
          </button>
        </div>
      </fieldset>
      {!canEdit ? <Notice tone="info">تعديل الإعدادات يتطلب صلاحية إدارة الأجهزة.</Notice> : null}
    </Card>
  );
}
